import { SelectProps } from '@chakra-ui/react';
import { UsaRegionCode, usaRegionCodeByRegionName } from '@common/location';
import {
    chakraComponents,
    InputActionMeta,
    Props,
    useChakraSelectProps,
} from 'chakra-react-select';
import { Variant } from 'chakra-react-select/dist/types/types';
import React, { useState } from 'react';
import GooglePlacesAutocomplete, {
    geocodeByAddress,
} from 'react-google-places-autocomplete';

type GooglePlacesAutocompleteValue = {
    label: string;
    value: {
        place_id: string;
    };
};

type InternalAddress = {
    address1: string;
    locality: string;
    region: string;
    postalCode: string;
};

type ExternalAddress = {
    address1: string;
    locality: string;
    region: UsaRegionCode | '';
    postalCode: string;
};

interface SelectAddressProps
    extends Omit<
            Props,
            | 'value'
            | 'onChange'
            | 'inputValue'
            | 'onInputChange'
            | 'size'
            | 'variant'
        >,
        Pick<SelectProps, 'size' | 'variant'> {
    value?: string;
    onChange?: (value: ExternalAddress) => void;
}

const GOOGLE_PLACES_API_KEY = process.env.NEXT_PUBLIC_GOOGLE_PLACES_API_KEY;
const DEFAULT_ADDRESS = {
    address1: '',
    locality: '',
    region: '',
    postalCode: '',
};

const SelectAddress = ({
    value,
    onChange,
    size = 'lgr',
    variant,
    ...props
}: SelectAddressProps) => {
    const [address, setAddress] = useState({
        ...DEFAULT_ADDRESS,
        address1: value ?? '',
    });

    const handleChange = async (value: GooglePlacesAutocompleteValue) => {
        const data = await geocodeByAddress(value.label);
        const address = mapAddressComponent(data[0].address_components);
        setAddress(address);
        onChange?.(formatAddress(address));
    };

    const onInputChange = (value: string, { action }: InputActionMeta) => {
        if (action !== 'input-change') return;
        setAddress({ ...address, address1: value });
        onChange?.({ ...formatAddress(address), address1: value });
    };

    const selectProps = useChakraSelectProps({
        ...props,
        variant: variant as Variant,
        components: {
            Menu,
            IndicatorSeparator: () => null,
            LoadingIndicator: () => null,
            LoadingMessage: () => null,
        },
        value: address.address1,
        onChange: handleChange as (value: unknown) => void,
        inputValue: address.address1,
        onInputChange,
        closeMenuOnSelect: false,
        chakraStyles: {
            control: (style) => ({ ...style, fontSize: size }),
            placeholder: (style) => ({ ...style, color: 'gray.300' }),
            menu: (style) => ({ ...style, zIndex: 100 }),
            menuList: (style) => ({
                ...style,
                paddingTop: 0,
                paddingBottom: 0,
            }),
        },
    });

    return (
        <GooglePlacesAutocomplete
            apiKey={GOOGLE_PLACES_API_KEY}
            apiOptions={{ language: 'en', region: 'us' }}
            autocompletionRequest={{
                componentRestrictions: { country: ['us'] },
                types: ['address'],
            }}
            // @ts-expect-error TODO: Fix typing
            selectProps={selectProps}
        />
    );
};

export default SelectAddress;

const getAddressKey = (types: string[]): keyof InternalAddress | null => {
    switch (types[0]) {
        case 'street_number':
        case 'route':
            return 'address1';
        case 'locality':
            return 'locality';
        case 'administrative_area_level_1':
            return 'region';
        case 'postal_code':
            return 'postalCode';
        default:
            return null;
    }
};

const mapAddressComponent = (
    addressComponents: google.maps.GeocoderAddressComponent[]
): InternalAddress =>
    addressComponents.reduce((acc, { long_name, types }) => {
        const key = getAddressKey(types);
        if (key === null) return acc;
        if (types[0] === 'route') {
            return { ...acc, [key]: `${acc[key]} ${long_name}` };
        }
        return { ...acc, [key]: long_name };
    }, DEFAULT_ADDRESS);

const formatAddress = (address: InternalAddress): ExternalAddress => {
    const { address1, locality, region, postalCode } = address;

    // N.B. Since this component is currently constrained to US addresses,
    // coerce the region to a US state code if it matches one.
    // If it doesn't, log a warning and return an empty string.
    let usaRegionCode: UsaRegionCode | '' = usaRegionCodeByRegionName[region];
    if (!usaRegionCode) {
        console.warn('Invalid US region code: ', region);
        usaRegionCode = '';
    }

    return {
        address1,
        locality,
        region: usaRegionCode,
        postalCode,
    };
};

const Menu = (props: any) => {
    if (props.options.length === 0) return null;
    return <chakraComponents.Menu {...props} />;
};
